// src/components/Modals/BankSelectionModal.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatCUIL } from '../../utils/documentFormatter';
import api from '../../services/api';

const BankSelectionModal = ({ client, onConfirm, onClose, loading = false }) => {
  const [selectedBank, setSelectedBank] = useState('');
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(true);
  const [banksError, setBanksError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [showValidation, setShowValidation] = useState(false);

  // Cargar bancos desde la API
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setBanksLoading(true);
        setBanksError(null);
        
        // Usar la ruta de la API que viste en las rutas
        const response = await api.get('/banks');
        
        if (response.data && Array.isArray(response.data)) {
          setBanks(response.data);
          setFilteredBanks(response.data);
        } else {
          setBanksError('Formato de respuesta inválido');
        }
      } catch (error) {
        console.error('Error cargando bancos:', error);
        setBanksError('Error al cargar la lista de bancos');
      } finally {
        setBanksLoading(false);
      }
    };

    fetchBanks();
  }, []);

  // Filtrar bancos basado en el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredBanks(banks);
    } else {
      const filtered = banks.filter(bank => 
        bank.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBanks(filtered);
    }
  }, [searchTerm, banks]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleBankSelect = (bank) => {
    setSelectedBank(bank.id);
    setSearchTerm(bank.name);
    setShowDropdown(false);
    setShowValidation(false); // Ocultar validación cuando se selecciona un banco
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);
    setShowValidation(false); // Ocultar validación cuando el usuario comienza a escribir
    
    // Si el valor coincide exactamente con un banco, seleccionarlo
    const exactMatch = banks.find(bank => 
      bank.name.toLowerCase() === value.toLowerCase()
    );
    if (exactMatch) {
      setSelectedBank(exactMatch.id);
    } else {
      setSelectedBank('');
    }
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleInputBlur = () => {
    // Delay para permitir clicks en el dropdown
    setTimeout(() => setShowDropdown(false), 150);
  };

  const handleConfirm = () => {
    if (selectedBank) {
      const selectedBankData = banks.find(bank => bank.id === parseInt(selectedBank));
      onConfirm({
        ...client,
        bank: {
          id: selectedBankData.id,
          name: selectedBankData.name,
          entity_number: selectedBankData.entity_number
        }
      });
    } else {
      // Mostrar validación solo cuando se intenta confirmar sin seleccionar banco
      setShowValidation(true);
    }
  };

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal fade text-left w-100 show" tabIndex="-1" style={{ display: 'block' }} aria-modal="true" role="dialog">
        <div className="modal-dialog modal-md modal-dialog-centered" style={{ minHeight: '60vh' }}>
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">Seleccionar Banco</h6>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <p>Selecciona el banco donde el cliente cobra su sueldo:</p>
              
              {/* Información del cliente */}
              <div className="card mb-3">
                <div className="card-body p-3">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-md me-3">
                      <img 
                        src={client.gender === 'F' ? '/assets/compiled/jpg/female-profile.jpg' : '/assets/compiled/jpg/male-profile.jpg'} 
                        alt="Avatar" 
                        className="rounded-circle" 
                      />
                    </div>
                    <div>
                      <h6 className="mb-0 text-primary">{client.name}</h6>
                      <small className="text-muted">CUIL: {formatCUIL(client.cuit)}</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Autocomplete de banco */}
              <div className="form-group">
                <label htmlFor="bankAutocomplete" className="form-label">Banco</label>
                {banksLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm me-2" />
                    Cargando bancos...
                  </div>
                ) : banksError ? (
                  <div className="alert alert-danger">
                    {banksError}
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={() => window.location.reload()}
                    >
                      Reintentar
                    </button>
                  </div>
                ) : (
                  <div className="position-relative">
                    <input
                      id="bankAutocomplete"
                      type="text"
                      className={`form-control ${selectedBank ? 'is-valid' : showValidation && !selectedBank ? 'is-invalid' : ''}`}
                      value={searchTerm}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      placeholder="Buscar banco..."
                      autoComplete="off"
                    />
                    
                    {/* Dropdown de resultados - Posicionado de forma absoluta para no afectar el layout */}
                    {showDropdown && (
                      <div 
                        className="dropdown-menu show position-absolute w-100" 
                        style={{ 
                          top: '100%',
                          left: '0',
                          maxHeight: '200px', 
                          overflowY: 'auto', 
                          zIndex: 1050,
                          marginTop: '0.125rem'
                        }}
                      >
                        {filteredBanks.length > 0 ? (
                          <>
                            {filteredBanks.slice(0, 5).map((bank) => (
                              <button
                                key={bank.id}
                                type="button"
                                className={`dropdown-item d-flex align-items-center w-100 text-start ${selectedBank === bank.id ? 'active' : ''}`}
                                onClick={() => handleBankSelect(bank)}
                                style={{ border: 'none', background: 'none', padding: '0.5rem 1rem' }}
                              >
                                <i className="bi bi-bank me-2 text-muted"></i>
                                <span>{bank.name}</span>
                              </button>
                            ))}
                            {filteredBanks.length > 5 && (
                              <div className="dropdown-item-text text-muted small border-top pt-2 mt-1">
                                <i className="bi bi-info-circle me-1"></i>
                                {filteredBanks.length - 5} resultado{filteredBanks.length - 5 !== 1 ? 's' : ''} adicional{filteredBanks.length - 5 !== 1 ? 'es' : ''}. Continúa escribiendo para refinar la búsqueda.
                              </div>
                            )}
                          </>
                        ) : searchTerm ? (
                          <div className="dropdown-item-text text-muted text-center py-3">
                            <div className="fw-medium">Sin resultados</div>
                            <small>No se encontraron bancos que coincidan con "<span className="fw-medium">{searchTerm}</span>"</small>
                          </div>
                        ) : (
                          <div className="dropdown-item-text text-muted text-center py-2">
                            <i className="bi bi-keyboard me-1"></i>
                            Comienza a escribir para buscar bancos
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Feedback visual */}
                    {showValidation && !selectedBank && (
                      <div className="invalid-feedback">
                        Por favor, selecciona un banco válido de la lista
                      </div>
                    )}
                    {selectedBank && (
                      <div className="valid-feedback">
                        <i className="bi bi-check-circle me-1"></i>
                        Banco seleccionado
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleConfirm}
                disabled={!selectedBank || loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Procesando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

BankSelectionModal.propTypes = {
  client: PropTypes.shape({
    name: PropTypes.string.isRequired,
    cuit: PropTypes.string.isRequired,
    gender: PropTypes.string
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default BankSelectionModal;
